"use client";

import { getMagrabiLocationIcon } from "@/apiUtils/commonUtils";
import {
  BookEyeTestContent,
  BookingDetails,
  EyeTestServiceType,
  StoreType,
  TranslationData,
} from "@/common/lib/types";
import GoogleMapComponent from "@/components/map/GoogleMap";
import useStoreService from "@/hooks/useStoreService";
import { cn } from "@/lib/tailwind";
import EyeTestDate from "@/widgets/EyeTestDate";
import EyeTestMyInfo from "@/widgets/EyeTestMyInfo";
import EyeTestSuccess, { EyeTestSuccessRef } from "@/widgets/EyeTestSuccess";
import EyeTestType from "@/widgets/EyeTestType";
import FindUs from "@/widgets/FindUs";
import Image from "next/image";
import { FC, ReactNode, useRef, forwardRef, useImperativeHandle } from "react";

interface BookEyeTestLayoutProps {
  slug: string;
  locale: string;
  pageContent?: BookEyeTestContent;
  selectedServiceType?: EyeTestServiceType;
  serviceId?: number; // ADD: Accept serviceId prop
  stores: StoreType[];
  storeDetails?: StoreType;
  translations?: TranslationData;
  bookingDetails?: BookingDetails;
  holidays?: Date[];
}

export interface BookEyeTestLayoutRef {
  triggerCancelModal: () => void;
}

const BookEyeTestLayout = forwardRef<BookEyeTestLayoutRef, BookEyeTestLayoutProps>((props, ref) => {
  const {
    slug,
    locale,
    stores,
    storeDetails,
    selectedServiceType,
    serviceId, // ADD: Extract serviceId
    translations,
    bookingDetails,
    pageContent,
    holidays,
  } = props;

  const eyeTestSuccessRef = useRef<EyeTestSuccessRef>(null);

  useImperativeHandle(ref, () => ({
    triggerCancelModal: () => {
      if (eyeTestSuccessRef.current) {
        eyeTestSuccessRef.current.openCancelModal();
      }
    },
  }));

  const {
    loading,
    sortedStores,
    selectedStore,
    currentLocation,
    markers,
    mapCenter,
    handleGetMyLocation,
    handleMarkerClick,
    handleScriptLoad,
    setSelectedStore,
    setSortedStores,
    calculateDistances,
    setShowCurrentLocation,
  } = useStoreService(locale, stores, translations?.findStore);

  const renderLeftPanelContent = () => {
    const contentMap: Record<string, ReactNode | null> = {
      type: <EyeTestType storeDetails={storeDetails} content={pageContent?.testTypeSection} />,
      store: (
        <FindUs
          stores={sortedStores}
          translations={translations?.findStore!}
          selectedServiceType={selectedServiceType}
          selectedStore={selectedStore}
          markers={markers}
          currentLocation={currentLocation}
          loading={loading}
          mapCenter={mapCenter}
          handleMarkerClick={handleMarkerClick}
          handleScriptLoad={handleScriptLoad}
          setSelectedStore={setSelectedStore}
          setSortedStores={setSortedStores}
          handleGetMyLocation={handleGetMyLocation}
          calculateDistances={calculateDistances}
          setShowCurrentLocation={setShowCurrentLocation}
        />
      ),
      date: (
        <EyeTestDate
          bookingDetails={bookingDetails} // ADD: Pass booking details
          serviceType={bookingDetails?.eyeTestType ?? selectedServiceType}
          serviceId={serviceId}
          storeId={storeDetails?.id}
          content={pageContent?.dateSection}
          bookingId={bookingDetails?.id}
          initialHolidays={holidays}
        />
      ),
      "my-info": (
        <EyeTestMyInfo
          serviceType={bookingDetails?.eyeTestType ?? selectedServiceType}
          storeDetails={storeDetails}
          content={pageContent?.myInfoSection}
          bookingDetails={bookingDetails}
        />
      ),
      success: (
        <EyeTestSuccess
          ref={eyeTestSuccessRef}
          bookingDetails={bookingDetails}
          storeDetails={storeDetails}
          content={pageContent?.successSection}
          locale={locale}
        />
      ),
    };

    return contentMap[slug] ?? null;
  };

  const renderRightPanelContent = () => {
    const contentMap: Record<string, ReactNode | null> = {
      type: (
        <Image
          src={pageContent?.testTypeSection?.media ?? ""}
          width={500}
          height={200}
          alt="type"
          className="h-full w-full"
        />
      ),
      store: (
        <GoogleMapComponent
          markers={markers}
          center={mapCenter}
          onMarkerClick={handleMarkerClick}
          onScriptLoad={handleScriptLoad}
          selectedMarkerId={selectedStore?.id}
          currentLocation={currentLocation}
          getMarkerIcon={getMagrabiLocationIcon}
          hasContainerStyle
        />
      ),
      date: (
        <Image
          src={pageContent?.dateSection?.media ?? ""}
          width={790}
          height={594}
          alt="date"
          className="h-full w-full"
        />
      ),
      "my-info": (
        <Image
          src={pageContent?.myInfoSection?.media ?? ""}
          width={790}
          height={594}
          alt="info"
          className="h-full w-full"
        />
      ),
      success: (
        <Image
          src={pageContent?.successSection?.media ?? ""}
          width={790}
          height={594}
          alt="success"
          className="h-full w-full"
        />
      ),
    };

    return contentMap[slug] ?? null;
  };

  return (
    <div
      className="z-0 mt-24 grid h-full w-full p-8 md:p-[4.5rem]"
      style={{ gridTemplateColumns: "repeat(9, 1fr)" }}
    >
      {/* LEFT PANEL */}
      <div
        className={cn(
          "col-span-9 md:col-span-3 md:ml-4",
          !storeDetails && "no-scrollbar overflow-y-auto",
        )}
      >
        {renderLeftPanelContent()}
      </div>
      {/* RIGHT PANEL */}
      <div
        className="col-span-6 hidden max-h-[37.125rem] gap-8 md:flex"
        style={{ marginRight: "4.5rem", marginLeft: "2rem" }}
      >
        {/* DIVIDER */}
        <div className={cn("h-full w-0 border bg-[#C4C4C4]", !storeDetails && "sticky top-28")} />
        <div className={"h-full flex-1"}>{renderRightPanelContent()}</div>
      </div>
    </div>
  );
});

export default BookEyeTestLayout;
